// ---------------------------------------------------------------------------
// Marketplace Schema (F16 Foundation)
// ---------------------------------------------------------------------------
// CROSS-TENANT BY DESIGN: Marketplace tables are intentionally visible across
// tenants. tenantId = owner/creator of the record. Listings with visibility
// "public" are browsable by all tenants. Purchases link buyer tenant to
// creator tenant via the listing. See Council Ruling CQ1.
//
// This does NOT violate tenant isolation — marketplace listings are explicitly
// shared by the creator. Query routes MUST filter by visibility = 'public'
// unless the requesting user belongs to the owner tenant.

import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const LISTING_STATUSES = ["draft", "pending_review", "published", "suspended", "archived"] as const;
export type ListingStatus = (typeof LISTING_STATUSES)[number];

export const LISTING_VISIBILITIES = ["public", "private", "unlisted"] as const;
export type ListingVisibility = (typeof LISTING_VISIBILITIES)[number];

export const REVIEW_MODERATION_STATUSES = ["pending", "approved", "flagged", "removed"] as const;
export type ReviewModerationStatus = (typeof REVIEW_MODERATION_STATUSES)[number];

export const REVENUE_SHARE_STATUSES = ["pending", "paid", "cancelled"] as const;
export type RevenueShareStatus = (typeof REVENUE_SHARE_STATUSES)[number];

export const SNAPSHOT_SCOPES = ["full", "pipelines", "workflows", "templates", "custom_fields", "pages"] as const;
export type SnapshotScope = (typeof SNAPSHOT_SCOPES)[number];

// ---------------------------------------------------------------------------
// JSONB type interfaces
// ---------------------------------------------------------------------------

export interface SnapshotConfigData {
  pipelines?: unknown[];
  workflows?: unknown[];
  emailTemplates?: unknown[];
  customFields?: unknown[];
  pageTemplates?: unknown[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// 1. Snapshots — Captured tenant configuration bundles
// ---------------------------------------------------------------------------

export const snapshots = pgTable("snapshots", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(), // Creator tenant
  name: text("name").notNull(),
  description: text("description"),
  scope: text("scope").notNull(), // SnapshotScope
  // configData can be large (full tenant config). Service layer enforces 5MB max.
  // configSizeBytes tracked for monitoring.
  configSizeBytes: integer("config_size_bytes"),
  version: integer("version").notNull().default(1),
  createdBy: uuid("created_by"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_snapshots_tenant").on(table.tenantId),
]);

// ---------------------------------------------------------------------------
// 2. Snapshot Versions — Immutable version history (append-only by design)
// ---------------------------------------------------------------------------

export const snapshotVersions = pgTable("snapshot_versions", {
  id: uuid("id").primaryKey().defaultRandom(),
  snapshotId: uuid("snapshot_id").notNull(),
  tenantId: uuid("tenant_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  configData: jsonb("config_data").$type<SnapshotConfigData>().notNull(),
  configHash: text("config_hash").notNull(), // SHA-256 of configData for dedup/integrity
  publishedAt: timestamp("published_at", tz), // When made available on marketplace (distinct from createdAt)
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // No updatedAt — versions are immutable
}, (table) => [
  index("idx_snap_ver_tenant").on(table.tenantId),
  index("idx_snap_ver_snapshot").on(table.tenantId, table.snapshotId),
  uniqueIndex("uq_snap_ver_snapshot_version").on(table.snapshotId, table.versionNumber),
]);

// ---------------------------------------------------------------------------
// 3. Marketplace Listings — Published snapshots available for purchase
// ---------------------------------------------------------------------------

export const marketplaceListings = pgTable("marketplace_listings", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(), // Creator tenant
  snapshotId: uuid("snapshot_id").notNull(),
  snapshotVersionId: uuid("snapshot_version_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  category: text("category"),
  tags: text("tags").array(),
  priceCents: integer("price_cents").notNull(), // Integer cents — no floating point
  currency: text("currency").notNull().default("EUR"),
  status: text("status").notNull().default("draft"), // ListingStatus
  // CQ1: Listings are NEVER cross-tenant-visible until explicitly published
  visibility: text("visibility").notNull().default("private"), // ListingVisibility
  moderationReason: text("moderation_reason"),
  publishedAt: timestamp("published_at", tz),
  reviewedAt: timestamp("reviewed_at", tz),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mkt_listing_tenant").on(table.tenantId),
  index("idx_mkt_listing_status").on(table.tenantId, table.status),
  index("idx_mkt_listing_visibility").on(table.visibility, table.status), // Cross-tenant browse query
  uniqueIndex("uq_mkt_listing_snapshot_ver").on(table.tenantId, table.snapshotId, table.snapshotVersionId),
]);

// ---------------------------------------------------------------------------
// 4. Marketplace Reviews — Buyer reviews with moderation (CQ5)
// ---------------------------------------------------------------------------

export const marketplaceReviews = pgTable("marketplace_reviews", {
  id: uuid("id").primaryKey().defaultRandom(),
  listingId: uuid("listing_id").notNull(),
  reviewerTenantId: uuid("reviewer_tenant_id").notNull(), // Buyer tenant
  rating: integer("rating").notNull(), // 1-5
  reviewText: text("review_text"),
  // CQ5: EU Digital Services Act + spam/abuse moderation
  moderationStatus: text("moderation_status").notNull().default("pending"), // ReviewModerationStatus
  moderationReason: text("moderation_reason"),
  moderatedAt: timestamp("moderated_at", tz),
  moderatedBy: uuid("moderated_by"),
  flaggedAt: timestamp("flagged_at", tz),
  flagReason: text("flag_reason"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mkt_review_listing").on(table.listingId),
  index("idx_mkt_review_reviewer").on(table.reviewerTenantId),
  uniqueIndex("uq_mkt_review_listing_reviewer").on(table.listingId, table.reviewerTenantId),
]);

// ---------------------------------------------------------------------------
// 5. Marketplace Purchases — Purchase records
// ---------------------------------------------------------------------------

export const marketplacePurchases = pgTable("marketplace_purchases", {
  id: uuid("id").primaryKey().defaultRandom(),
  listingId: uuid("listing_id").notNull(),
  buyerTenantId: uuid("buyer_tenant_id").notNull(), // CQ1: tenantId on purchase = buyer
  creatorTenantId: uuid("creator_tenant_id").notNull(), // Denormalized for revenue share queries
  // Price captured at purchase time — listing price may change later
  pricePaidCents: integer("price_paid_cents").notNull(),
  currency: text("currency").notNull().default("EUR"),
  transactionRef: text("transaction_ref"), // External payment reference
  deployedAt: timestamp("deployed_at", tz), // When buyer deployed the snapshot
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_mkt_purchase_buyer").on(table.buyerTenantId),
  index("idx_mkt_purchase_creator").on(table.creatorTenantId),
  index("idx_mkt_purchase_listing").on(table.listingId),
  uniqueIndex("uq_mkt_purchase_listing_buyer").on(table.listingId, table.buyerTenantId),
]);

// ---------------------------------------------------------------------------
// 6. Revenue Shares — Revenue distribution to snapshot creators
// ---------------------------------------------------------------------------

export const revenueShares = pgTable("revenue_shares", {
  id: uuid("id").primaryKey().defaultRandom(),
  purchaseId: uuid("purchase_id").notNull(),
  creatorTenantId: uuid("creator_tenant_id").notNull(), // Who receives the share
  platformFeeCents: integer("platform_fee_cents").notNull(),
  creatorShareCents: integer("creator_share_cents").notNull(),
  currency: text("currency").notNull().default("EUR"),
  commissionRate: text("commission_rate").notNull(), // e.g., "0.70" (70% to creator)
  status: text("status").notNull().default("pending"), // RevenueShareStatus
  // CQ2: Audit trail for commission computation
  calculatedAt: timestamp("calculated_at", tz).notNull().defaultNow(),
  calculationVersion: text("calculation_version").notNull().default("v1"),
  paidAt: timestamp("paid_at", tz),
  payoutBatchId: text("payout_batch_id"), // Monthly batch grouping
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_rev_share_creator").on(table.creatorTenantId),
  index("idx_rev_share_purchase").on(table.purchaseId),
  index("idx_rev_share_status").on(table.creatorTenantId, table.status),
]);
